const REFERENCE_ATTRIBUTES = ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'form', 'list'];
const URL_REFERENCE_ATTRIBUTES = ['fill', 'stroke', 'clip-path', 'mask', 'filter'];
const RESPONSIVE_IMAGE_WIDTHS = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
const MEDIA_SELECTION_SETTLE_FRAMES = 3;
const MEDIA_SELECTION_MAX_FRAMES = 30;

const nextAnimationFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

/**
 * Initializes a normal full Product card to a specific collection variant.
 *
 * The actual UI remains the theme's shared <product-card> block tree. This
 * wrapper only seeds and preserves the represented variant state.
 */
class VariantProductCard extends HTMLElement {
  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this._childObserver?.disconnect();
    this._stateObserver?.disconnect();
    this._childObserver = null;
    this._stateObserver = null;
  }

  async waitForProductCard() {
    const existing = this.querySelector('product-card');
    if (existing instanceof HTMLElement) return existing;

    return new Promise((resolve) => {
      const finish = (card) => {
        this._childObserver?.disconnect();
        this._childObserver = null;
        resolve(card);
      };

      this._childObserver = new MutationObserver(() => {
        const card = this.querySelector('product-card');
        if (card instanceof HTMLElement) finish(card);
      });

      this._childObserver.observe(this, { childList: true, subtree: true });

      setTimeout(() => {
        if (this._childObserver) finish(this.querySelector('product-card'));
      }, 3000);
    });
  }

  async initialize() {
    if (this.dataset.initialized === 'true' || this.dataset.initializing === 'true') return;

    const variantId = this.dataset.variantId;
    if (!variantId) {
      this.reveal();
      return;
    }

    this.dataset.initializing = 'true';
    const productCard = await this.waitForProductCard();

    if (!(productCard instanceof HTMLElement)) {
      delete this.dataset.initializing;
      this.reveal();
      return;
    }

    try {
      this.remapIds(variantId);
      await this.applyVariantState(productCard);
      this.observeCardState();

      this.dataset.initialized = 'true';
    } catch (error) {
      console.error('Unable to initialize collection variant product card', error);
    } finally {
      delete this.dataset.initializing;
      this.reveal();
    }
  }

  async applyVariantState(productCard) {
    if (this._applyingVariant) return;
    this._applyingVariant = true;

    // Avoid recursively reacting to our own DOM writes.
    this._stateObserver?.disconnect();

    try {
      this.updateLinks(productCard);
      this.updateTitle(productCard);
      this.updatePrice(productCard);
      this.updateSku(productCard);
      this.updateForms(productCard);
      this.updatePurchaseControls(productCard);
      this.updateTransitionMedia(productCard);
      this.updateVariantPicker(productCard);
      await this.updateMedia(productCard);
    } finally {
      this._applyingVariant = false;
      if (this._stateObserver) this.startStateObserver();
    }
  }

  observeCardState() {
    this._stateObserver?.disconnect();

    this._stateObserver = new MutationObserver(() => {
      if (this._applyingVariant) return;
      void this.synchronizeCurrentCard();
    });

    this.startStateObserver();
  }

  async synchronizeCurrentCard() {
    const currentCard = this.querySelector('product-card');
    const variantId = this.dataset.variantId;
    if (!(currentCard instanceof HTMLElement) || !variantId) return;

    this.remapIds(variantId);
    await this.applyVariantState(currentCard);
    this.dataset.initialized = 'true';
    this.reveal();
  }

  startStateObserver() {
    this._stateObserver?.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'data-variant-id',
        'data-variant-url',
        'data-variant-title',
        'data-parent-product-title',
        'data-variant-sku',
        'data-variant-available',
        'data-variant-price',
        'data-variant-sold-out-text',
        'data-variant-media-id',
        'data-variant-image-url',
        'data-quantity-min',
        'data-quantity-increment',
        'data-quantity-max',
      ],
    });
  }

  remapIds(variantId) {
    const suffix = `-variant-${variantId}`;
    const idMap = new Map();

    this.querySelectorAll('[id]').forEach((element) => {
      // HTMLFormElement exposes named controls as properties. Because Shopify
      // product forms contain an input named "id", form.id can resolve to that
      // input instead of the literal id attribute.
      const oldId = element.getAttribute('id');
      if (!oldId || oldId.endsWith(suffix)) return;

      const newId = `${oldId}${suffix}`;
      idMap.set(oldId, newId);
      element.setAttribute('id', newId);
    });

    if (idMap.size === 0) return;

    this.querySelectorAll('*').forEach((element) => {
      for (const attribute of REFERENCE_ATTRIBUTES) {
        const value = element.getAttribute(attribute);
        if (!value) continue;

        const updated = value
          .split(/\s+/)
          .map((token) => idMap.get(token) ?? token)
          .join(' ');

        if (updated !== value) element.setAttribute(attribute, updated);
      }

      for (const attribute of ['href', 'xlink:href']) {
        const value = element.getAttribute(attribute);
        if (!value?.startsWith('#')) continue;

        const oldId = value.slice(1);
        const newId = idMap.get(oldId);
        if (newId) element.setAttribute(attribute, `#${newId}`);
      }

      for (const attribute of URL_REFERENCE_ATTRIBUTES) {
        const value = element.getAttribute(attribute);
        if (!value?.includes('url(#')) continue;

        let updated = value;
        for (const [oldId, newId] of idMap) {
          updated = updated.replaceAll(`url(#${oldId})`, `url(#${newId})`);
        }
        if (updated !== value) element.setAttribute(attribute, updated);
      }
    });
  }

  updateLinks(productCard) {
    const variantUrl = this.dataset.variantUrl;
    if (!variantUrl) return;

    productCard
      .querySelectorAll("a[ref='productCardLink'], a[ref='productTitleLink'], a[ref='cardGalleryLink']")
      .forEach((link) => {
        if (link instanceof HTMLAnchorElement) link.href = variantUrl;
      });
  }

  updateTitle(productCard) {
    const title = this.dataset.variantTitle;
    if (!title) return;

    const mainLink = productCard.querySelector("a[ref='productCardLink']");
    const hiddenTitle = mainLink?.querySelector('.visually-hidden');
    const parentTitle = this.dataset.parentProductTitle || hiddenTitle?.textContent?.trim() || '';

    if (hiddenTitle) hiddenTitle.textContent = title;

    productCard.querySelectorAll("[ref='productTitleLink']").forEach((link) => {
      const paragraph = link.querySelector('p');
      const titleText = link.querySelector('.title-text');

      if (paragraph) paragraph.textContent = title;
      else if (titleText) titleText.textContent = title;

      if (link instanceof HTMLAnchorElement) link.setAttribute('aria-label', title);
    });

    productCard.querySelectorAll('.product-grid-view-zoom-out--details h3').forEach((heading) => {
      heading.textContent = title;
    });

    productCard.querySelectorAll("a[ref='cardGalleryLink']").forEach((link) => link.setAttribute('aria-label', title));

    // Product-card layouts can use a generic Text block connected to the
    // product title instead of the dedicated product-title block.
    if (parentTitle && parentTitle !== title) {
      const walker = document.createTreeWalker(productCard, NodeFilter.SHOW_TEXT);
      const nodes = [];
      let node = walker.nextNode();

      while (node) {
        const parent = node.parentElement;
        if (node.nodeValue?.trim() === parentTitle && parent && !parent.closest('script, style, template')) {
          nodes.push(node);
        }
        node = walker.nextNode();
      }

      nodes.forEach((textNode) => {
        const leading = textNode.nodeValue?.match(/^\s*/)?.[0] ?? '';
        const trailing = textNode.nodeValue?.match(/\s*$/)?.[0] ?? '';
        textNode.nodeValue = `${leading}${title}${trailing}`;
      });
    }
  }

  updatePrice(productCard) {
    const template = this.querySelector('template[data-variant-price-template]');
    const source = template?.content.querySelector('[data-variant-price-content]');
    if (!(source instanceof HTMLElement)) return;

    productCard.querySelectorAll("product-price [ref='priceContainer']").forEach((priceContainer) => {
      if (priceContainer.innerHTML !== source.innerHTML) {
        priceContainer.innerHTML = source.innerHTML;
      }
    });

    productCard.querySelectorAll('product-price .volume-pricing-note').forEach((note) => note.remove());
  }

  updateSku(productCard) {
    const sku = this.dataset.variantSku ?? '';

    productCard.querySelectorAll("product-sku-component [ref='skuContainer']").forEach((container) => {
      const prefix = container.textContent?.includes(':') ? `${container.textContent.split(':')[0]}: ` : '';
      const value = sku ? `${prefix}${sku}` : '';
      if (container.textContent !== value) container.textContent = value;
    });
  }

  updateForms(productCard) {
    const variantId = this.dataset.variantId;
    const available = this.dataset.variantAvailable === 'true';
    if (!variantId) return;

    productCard.querySelectorAll("input[name='id'], input[ref='variantId']").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = variantId;
      input.disabled = !available;
    });

    productCard.querySelectorAll('product-form-component').forEach((productForm) => {
      if (!(productForm instanceof HTMLElement)) return;
      if (this.dataset.variantUrl) productForm.dataset.productUrl = this.dataset.variantUrl;
      productForm.dataset.quantityDefault = this.dataset.quantityMin || '1';
    });

    productCard.querySelectorAll("quantity-selector-component[ref='quantitySelector']").forEach((quantitySelector) => {
      if (quantitySelector instanceof HTMLElement) quantitySelector.dataset.variantId = variantId;
    });

    productCard.querySelectorAll("input[name='quantity'][ref='quantityInput']").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const minimum = this.dataset.quantityMin || '1';
      input.value = minimum;
      input.min = minimum;
      input.dataset.min = minimum;
      input.step = this.dataset.quantityIncrement || '1';
      input.disabled = !available;

      if (this.dataset.quantityMax) input.max = this.dataset.quantityMax;
      else input.removeAttribute('max');
    });

    productCard.querySelectorAll('quick-add-component').forEach((quickAdd) => {
      quickAdd.dataset.quickAddButton = 'add';
    });
  }

  updatePurchaseControls(productCard) {
    const available = this.dataset.variantAvailable === 'true';
    const variantPrice = this.dataset.variantPrice;

    productCard.querySelectorAll("button[ref='addToCartButton']").forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = !available;
      const icon = button.querySelector('.add-to-cart-icon');
      if (icon instanceof HTMLElement) icon.hidden = !available;

      const price = button.querySelector('.add-to-cart-price');
      if (price && variantPrice) price.textContent = variantPrice;

      if (!available) {
        const content = button.querySelector('.add-to-cart-text__content > span > span');
        if (content) content.textContent = this.dataset.variantSoldOutText || 'Sold out';
      }
    });

    productCard.querySelectorAll('add-to-cart-component').forEach((addToCart) => {
      if (!(addToCart instanceof HTMLElement)) return;
      if (this.dataset.variantImageUrl) addToCart.dataset.productVariantMedia = this.dataset.variantImageUrl;
    });
  }

  updateTransitionMedia(productCard) {
    const imageUrl = this.dataset.variantImageUrl;
    if (!imageUrl) return;

    const transitionWrapper = productCard.closest('product-card-link');
    transitionWrapper?.setAttribute('data-featured-media-url', imageUrl);
  }

  updateVariantPicker(productCard) {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const inputs = Array.from(productCard.querySelectorAll('input[data-variant-id]'));
    const matchingInput = inputs.find(
      (input) =>
        input instanceof HTMLInputElement &&
        (input.dataset.variantId === variantId || input.dataset.firstAvailableOrFirstVariantId === variantId)
    );

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const selected = input === matchingInput;
      input.checked = selected;
      input.dataset.currentChecked = selected ? 'true' : 'false';
      input.toggleAttribute('checked', selected);
    });

    productCard.removeAttribute('data-no-swatch-selected');
  }

  imageUrlAtWidth(source, width) {
    if (!source) return '';

    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return source;
    }
  }

  seedVariantImage(targetSlide) {
    const imageUrl = this.dataset.variantImageUrl;
    if (!imageUrl || !(targetSlide instanceof HTMLElement)) return;

    const image = targetSlide.querySelector('.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const src = this.imageUrlAtWidth(imageUrl, 1200);
    const srcset = RESPONSIVE_IMAGE_WIDTHS.map((width) => `${this.imageUrlAtWidth(imageUrl, width)} ${width}w`).join(', ');

    if (image.src !== src) image.src = src;
    if (image.srcset !== srcset) image.srcset = srcset;
    image.dataset.maxResolution = this.imageUrlAtWidth(imageUrl, 3840);

    const media = targetSlide.querySelector('.product-media');
    if (media instanceof HTMLElement && this.dataset.variantMediaId) {
      media.dataset.mediaId = this.dataset.variantMediaId;
    }
  }

  /**
   * Selects the represented variant after the slideshow controller has finished
   * upgrading and initializing. Slideshow setup schedules its initial selection
   * over multiple frames, so require the variant selection to remain stable
   * before considering the card hydrated.
   *
  * This also covers collection filter morphs: component refs are refreshed in
  * a microtask after the morph, and the loop retries against those fresh refs.
  */
  async selectVariantMedia(productCard, slideshow, targetSlide, mediaId) {
    let settledFrames = 0;

    for (let frame = 0; frame < MEDIA_SELECTION_MAX_FRAMES; frame += 1) {
      await nextAnimationFrame();

      if (
        !this.isConnected ||
        !this.contains(productCard) ||
        !productCard.contains(slideshow) ||
        !slideshow.contains(targetSlide)
      ) {
        return;
      }

      // A gallery morph can replace the image or restore hidden attributes
      // while slideshow and product-card refs are being refreshed.
      this.seedVariantImage(targetSlide);
      targetSlide.hidden = false;

      slideshow.querySelectorAll('slideshow-slide[variant-image]').forEach((variantSlide) => {
        if (variantSlide !== targetSlide) variantSlide.hidden = true;
      });

      if (typeof slideshow.select !== 'function' || !Array.isArray(slideshow.slides)) {
        settledFrames = 0;
        continue;
      }

      await slideshow.select({ id: mediaId }, undefined, { animate: false });

      const selectedSlide = slideshow.slides?.[slideshow.current];
      const selectedMediaId = selectedSlide?.getAttribute('slide-id');

      if (selectedSlide === targetSlide || selectedMediaId === mediaId) {
        settledFrames += 1;
        if (settledFrames >= MEDIA_SELECTION_SETTLE_FRAMES) return;
      } else {
        settledFrames = 0;
      }
    }

    // Keep the correct bitmap in place even if an unrelated slideshow error
    // prevents the controller from reporting a stable selected slide.
    this.seedVariantImage(targetSlide);
  }

  async updateMedia(productCard) {
    const mediaId = this.dataset.variantMediaId;
    const imageUrl = this.dataset.variantImageUrl;
    if (!mediaId && !imageUrl) return;

    let slideshow = productCard.querySelector("slideshow-component[ref='slideshow'], slideshow-component");
    if (!slideshow) return;

    let targetSlide = mediaId ? slideshow.querySelector(`slideshow-slide[slide-id='${CSS.escape(mediaId)}']`) : null;

    // Product card galleries intentionally omit many hidden variant images for
    // performance. Reuse the first rendered slide and seed it with the exact
    // variant image when that media wasn't rendered.
    if (!(targetSlide instanceof HTMLElement)) {
      targetSlide = slideshow.querySelector('slideshow-slide:not([hidden])') || slideshow.querySelector('slideshow-slide');
      if (targetSlide instanceof HTMLElement && mediaId) {
        targetSlide.setAttribute('slide-id', mediaId);
        targetSlide.setAttribute('variant-image', '');
      }
    }

    if (!(targetSlide instanceof HTMLElement)) return;

    this.seedVariantImage(targetSlide);
    targetSlide.hidden = false;

    slideshow.querySelectorAll('slideshow-slide[variant-image]').forEach((variantSlide) => {
      if (variantSlide !== targetSlide) variantSlide.hidden = true;
    });

    if (mediaId) await this.selectVariantMedia(productCard, slideshow, targetSlide, mediaId);
  }

  reveal() {
    this.setAttribute('ready', '');
  }
}

if (!customElements.get('variant-product-card')) {
  customElements.define('variant-product-card', VariantProductCard);
}
